import { focus } from '@dojo/framework/core/middleware/focus';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { Tooltip } from '@dojo/widgets/tooltip';
import { Icon } from '@holmescorp/base-widgets';
import { CalculationsButton, CaseStudyButton, DiscussionButton, ExerciseButton, FlashCardsButton, QuizButton, TopicTitle } from '@holmescorp/book-widgets';
import { Keys } from '@holmescorp/common';
import { createNlsMiddleware, keymap, lccstate, pathmap, quizmap } from '@holmescorp/lcc-state';
import css from '../../css/expanded.m.css';
import { ExpandedBaseCard } from './expandedBase';
import { ShortDescription } from './shortdesc';
import { formatAriaProperties } from '@dojo/widgets/common/util';
const icache = createICacheMiddleware();
const nls = createNlsMiddleware('ditabook');
const settings = createNlsMiddleware('settings');
const factory = create({
    icache,
    nls,
    settings,
    theme,
    focus,
    lccstate
}).properties();
export const ExpandedTopicCard = factory(({ id, properties, middleware: { nls, settings, icache, theme, focus, lccstate } }) => {
    const { expanded, height, topic, iconUrl, xqa, onRequestClose } = properties();
    const themeCss = theme.classes(css);
    const read_ttStatus = () => icache.set('read_ttopen', !icache.get('read_ttopen'));
    const quiz_ttStatus = () => icache.set('quiz_ttopen', !icache.get('quiz_ttopen'));
    const calc_ttStatus = () => icache.set('calc_ttopen', !icache.get('calc_ttopen'));
    const exer_ttStatus = () => icache.set('exer_ttopen', !icache.get('exer_ttopen'));
    if (topic) {
        const { attributes = {}, children = [] } = topic;
        const { title, key } = attributes;
        const onlytg = children.filter((val) => !('shortdesc' in val));
        const { quiz, flashcards, discussion, casestudies, calculations, exercises, titles = {} } = quizmap.get(key) || {};
        const hasQuiz = Boolean(quiz) || Boolean(flashcards);
        const { messages: { quiz: quizText = 'Quiz', flashcards: flashcardText = 'Flashcards', discussions: discussionsText = 'Discussions', activity_intro: activityIntro = 'Choose', teach_me: teachMe = 'Teach Me', teach_me_tooltip: teachMeTooltip, quiz_me: quizMe = 'Quiz Me', quiz_me_tooltip: quizMeTooltip, calc_me: calcMe = 'Calculations', calc_me_tooltip: calcMeTooltip, exer_me: exerMe = 'Exercises', exer_me_tooltip: exerMeTooltip } } = nls();
        const sd = children.filter((val) => 'shortdesc' in val);
        const shortdesc = sd.length > 0 ? sd[0] : { shortdesc: '' };
        const calcs = calculations && calculations.length
            ? calculations.map((quizid) => {
                const topicid = key;
                const title = titles[quizid];
                return w(CalculationsButton, { topicid, quizid, title });
            })
            : [];
        const exes = exercises && exercises.length
            ? exercises.map((quizid) => {
                const topicid = key;
                const title = titles[quizid];
                return w(ExerciseButton, { topicid, quizid, title });
            })
            : [];
        const caseStudies = casestudies && casestudies.length
            ? casestudies.map((casestudyid) => {
                return v('div', { role: 'listitem' }, [
                    w(CaseStudyButton, {
                        topicid: key,
                        quizid: casestudyid
                    })
                ]);
            })
            : [];
        const activityBlock = hasQuiz
            ? [
                flashcards
                    ? v('div', { role: 'listitem' }, [
                        w(FlashCardsButton, { topicid: key, text: flashcardText })
                    ])
                    : null,
                discussion
                    ? v('div', { role: 'listitem' }, [
                        w(DiscussionButton, { topicid: key, text: discussionsText })
                    ])
                    : null,
                ...caseStudies
            ]
            : [];
        const { messages: settingsMessages } = settings();
        let allowed_topics = [];
        const dat = settingsMessages.demo_allowed_topics;
        if (dat) {
            try {
                allowed_topics = JSON.parse(dat);
            }
            catch (e) {
                console.warn(`json parse error ${e}`, dat);
            }
        }
        const maybeFocus = (key) => {
            const tracking = lccstate.get(lccstate.path('tracking'));
            if (tracking && key === tracking.topicgroup) {
                focus.focus();
            }
        };
        const topicgroups = onlytg.map((val) => {
            const { attributes = {} } = val['topicgroup'];
            const { title, key, readtime } = attributes;
            return v('div', {
                key: `${key}-topicgroup`,
                xqa: `topicgroup ${key}`,
                role: 'button',
                classes: [themeCss.item],
                tabIndex: 0,
                focus: () => focus.shouldFocus(),
                onFocus: maybeFocus(key),
                onclick: (event) => {
                    event.stopPropagation();
                    if (settingsMessages.is_demo === 'true' && allowed_topics.indexOf(key) === -1) {
                        return;
                    }
                    const { path } = keymap.get(key);
                    const { view } = pathmap.get(path);
                    view();
                },
                onkeydown: (event) => {
                    const { path } = keymap.get(key);
                    const { view } = pathmap.get(path);
                    switch (event.key) {
                        case Keys.Enter:
                        case Keys.Space:
                        case Keys.Spacebar:
                            event.preventDefault();
                            if (settingsMessages.is_demo === 'true' && allowed_topics.indexOf(key) === -1) {
                                return;
                            }
                            view();
                    }
                }
            }, [v('div', {}, [w(TopicTitle, { title, key, useTitle: true, readtime })])]);
        });
        const extraClasses = icache.get('read_ttopen') || icache.get('quiz_ttopen') ? [themeCss.skelWithTooltip] : [];
        return w(ExpandedBaseCard, {
            underlay: true,
            expanded,
            widgetId: `${key}-${id}`,
            title,
            iconUrl,
            xqa,
            height,
            onRequestClose,
            extraClasses
        }, [
            {
                content: v('div', { classes: [themeCss.twoColumn] }, [
                    v('leading', {}, [
                        w(ShortDescription, { topicdata: shortdesc.shortdesc }),
                        v('h3', { classes: [themeCss.activity_intro] }, [activityIntro]),
                        v('div', { classes: [themeCss.topicgroups] }, [
                            v('h4', { classes: [themeCss.teach_me] }, [
                                teachMe,
                                teachMeTooltip
                                    ? w(Tooltip, {
                                        open: icache.getOrSet('read_ttopen', false),
                                        orientation: 'top'
                                    }, [
                                        {
                                            content: v('span', { id: 'teach-me-tooltip' }, [
                                                teachMeTooltip
                                            ]),
                                            trigger: v('span', {
                                                onclick: read_ttStatus,
                                                onkeydown: (event) => {
                                                    switch (event.key) {
                                                        case Keys.Enter:
                                                        case Keys.Space:
                                                        case Keys.Spacebar:
                                                            read_ttStatus();
                                                            break;
                                                    }
                                                },
                                                role: 'button',
                                                tabIndex: 0,
                                                ...formatAriaProperties({
                                                    label: 'Open description for the Teach Me section',
                                                    describedby: 'teach-me-tooltip'
                                                })
                                            }, [w(Icon, { fapre: 'far', faname: 'circle-question' })])
                                        }
                                    ])
                                    : null
                            ]),
                            v('book', {}, [w(Icon, { faname: 'book-open', size: '2x' })]),
                            v('items', {}, [...topicgroups])
                        ]),
                        v('div', { classes: [themeCss.topicquiz] }, [
                            v('h4', { classes: [themeCss.quiz_me] }, [
                                quizMe,
                                quizMeTooltip
                                    ? w(Tooltip, {
                                        open: icache.getOrSet('quiz_ttopen', false),
                                        orientation: 'top'
                                    }, [
                                        {
                                            content: v('span', { id: 'quiz-me-tooltip' }, [
                                                quizMeTooltip
                                            ]),
                                            trigger: v('span', {
                                                onclick: quiz_ttStatus,
                                                onkeydown: (event) => {
                                                    switch (event.key) {
                                                        case Keys.Enter:
                                                        case Keys.Space:
                                                        case Keys.Spacebar:
                                                            quiz_ttStatus();
                                                            break;
                                                    }
                                                },
                                                role: 'button',
                                                tabIndex: 0,
                                                ...formatAriaProperties({
                                                    label: 'Open description for the Quiz section',
                                                    describedby: 'quiz-me-tooltip'
                                                })
                                            }, [w(Icon, { fapre: 'far', faname: 'circle-question' })])
                                        }
                                    ])
                                    : null
                            ]),
                            quiz
                                ? v('div', { role: 'listitem' }, [
                                    w(QuizButton, {
                                        topicid: key,
                                        text: quizText,
                                        showstatusicon: true
                                    })
                                ])
                                : null
                        ]),
                        calcs && calcs.length > 0
                            ? v('div', { classes: [themeCss.topiccalc] }, [
                                v('h4', { classes: [themeCss.calc_me] }, [
                                    calcMe,
                                    calcMeTooltip
                                        ? w(Tooltip, {
                                            open: icache.getOrSet('calc_ttopen', false),
                                            orientation: 'top'
                                        }, [
                                            {
                                                content: v('span', { id: 'calc-me-tooltip' }, [
                                                    calcMeTooltip
                                                ]),
                                                trigger: v('span', {
                                                    onclick: calc_ttStatus,
                                                    onkeydown: (event) => {
                                                        switch (event.key) {
                                                            case Keys.Enter:
                                                            case Keys.Space:
                                                            case Keys.Spacebar:
                                                                calc_ttStatus();
                                                                break;
                                                        }
                                                    },
                                                    role: 'button',
                                                    tabIndex: 0,
                                                    ...formatAriaProperties({
                                                        label: 'Open description for the Calculations section',
                                                        describedby: 'calc-me-tooltip'
                                                    })
                                                }, [
                                                    w(Icon, {
                                                        fapre: 'far',
                                                        faname: 'circle-question'
                                                    })
                                                ])
                                            }
                                        ])
                                        : null
                                ]),
                                v('calculator', {}, [w(Icon, { faname: 'calculator', size: '2x' })]),
                                v('items', {}, [...calcs])
                            ])
                            : null,
                        exes && exes.length
                            ? v('div', { classes: [themeCss.topicexer] }, [
                                v('h4', { classes: [themeCss.exer_me] }, [
                                    exerMe,
                                    exerMeTooltip
                                        ? w(Tooltip, {
                                            open: icache.getOrSet('exer_ttopen', false),
                                            orientation: 'top'
                                        }, [
                                            {
                                                content: v('span', { id: 'exer-me-tooltip' }, [
                                                    exerMeTooltip
                                                ]),
                                                trigger: v('span', {
                                                    onclick: exer_ttStatus,
                                                    onkeydown: (event) => {
                                                        switch (event.key) {
                                                            case Keys.Enter:
                                                            case Keys.Space:
                                                            case Keys.Spacebar:
                                                                exer_ttStatus();
                                                                break;
                                                        }
                                                    },
                                                    role: 'button',
                                                    tabIndex: 0,
                                                    ...formatAriaProperties({
                                                        label: 'Open description for the Exercises section',
                                                        describedby: 'exer-me-tooltip'
                                                    })
                                                }, [
                                                    w(Icon, {
                                                        fapre: 'far',
                                                        faname: 'circle-question'
                                                    })
                                                ])
                                            }
                                        ])
                                        : null
                                ]),
                                v('pencil', {}, [w(Icon, { faname: 'pencil', size: '2x' })]),
                                v('items', {}, [...exes])
                            ])
                            : null
                    ]),
                    v('trailing', {}, [...activityBlock])
                ])
            }
        ]);
    }
    return null;
});
export default ExpandedTopicCard;
